import {
    IApiAxiosError,
    IIndustry,
    IOrganization,
    updateOrganizationById,
} from "@/api";
import { IndustryAutocomplete } from "@/components/shared/IndustryAutocomplete";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";
import { useEffect, useState } from "react";

interface IProps {
    organization: IOrganization;
}

export const EditOrganizationDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<IProps>) => {
    const queryClient = useQueryClient();
    const { organization } = payload;

    const [name, setName] = useState(organization.name);
    const [industry, setIndustry] = useState<IIndustry | null>(
        organization.industry ?? null
    );

    // Reset form when organization changes
    useEffect(() => {
        setName(organization.name);
        setIndustry(organization.industry ?? null);
    }, [organization]);

    const { mutate, isPending, isSuccess, error } = useMutation({
        mutationFn: async () =>
            updateOrganizationById(organization.id, {
                name: name.trim(),
                industry_id: industry?.id ?? null,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-organizations"],
            });
            queryClient.invalidateQueries({
                queryKey: ["edit-organization-info"],
            });
            queryClient.invalidateQueries({
                queryKey: ["admin-organization-details"],
            });
            onClose();
        },
    });

    const axiosError = error as IApiAxiosError | null;

    const handleSave = () => {
        if (!name.trim()) return;
        mutate();
    };

    return (
        <Modal
            open={open}
            onClose={() => onClose()}
            title="Edit Organization"
            subtitle="Update the organization details below"
            maxWidth="sm"
            isLoading={isPending}
            actions={[
                {
                    label: "Save Changes",
                    onClick: handleSave,
                    disabled: !name.trim() || isPending || isSuccess,
                    color: "primary",
                },
            ]}
            footerText={
                axiosError && (
                    <Typography
                        color="error"
                        variant="caption"
                        sx={{
                            fontSize: "0.75rem",
                        }}
                    >
                        {axiosError.response?.data.detail ||
                            "An error occurred while updating the organization."}
                    </Typography>
                )
            }
        >
            <Stack gap={3}>
                <TextField
                    label="Organization Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoFocus
                />

                <IndustryAutocomplete
                    value={industry}
                    onChange={setIndustry}
                    helperText="Optional - Associates this organization with an industry"
                />
            </Stack>
        </Modal>
    );
};
